/* eslint-disable */
/* ---------- AI AUTOMATION EXPLAINER — educational animated demo ----------
   Exports: AutomateExplainer
   A terminal-style, ASCII-flavored demo that cycles through real business
   workflows and shows what "AI automation" actually looks like: a manual
   pain on the left, an AI agent working through it in the middle, and the
   automated result on the right. Educational, then funnels to ReadyIQ.

   WHY HTML/CSS (not SVG): every label lives in a real DOM box that reflows
   inside its container, so text can NEVER escape its border — the failure
   mode SVG fixed-coordinate text has. All copy is clamped + word-wrapped.

   prefers-reduced-motion: renders the first automation fully resolved,
   static, no cycling.
   ------------------------------------------------------------------------ */

import React, { useEffect, useRef, useState } from 'react';

const AUTOMATIONS = [
  {
    id: 'inbox', tag: 'inbox triage', cmd: 'triage --inbox',
    before: ['47 unread', '3 urgent buried', 'nobody on it till 9am'],
    steps: ['read + classify every thread', 'draft on-brand replies', 'escalate only the edge cases'],
    after: ['inbox at zero', '6 flagged for a human', 'replies sent in seconds'],
    saved: '~2.5 hrs / day',
  },
  {
    id: 'invoice', tag: 'invoice processing', cmd: 'process --invoices',
    before: ['a stack of PDFs', 'line items by hand', 'month-end crunch'],
    steps: ['extract every line item', 'match against POs', 'post to the ledger'],
    after: ['booked + reconciled', 'exceptions only to you', 'audit trail written'],
    saved: '~12 hrs / week',
  },
  {
    id: 'leads', tag: 'lead follow-up', cmd: 'follow-up --leads',
    before: ['new lead, 9:47pm', 'nobody awake', 'cold by morning'],
    steps: ['qualify the intent', 'answer their questions', 'book the call'],
    after: ['meeting on the calendar', 'before you wake up', 'lead never went cold'],
    saved: 'dozens of leads / mo',
  },
  {
    id: 'reports', tag: 'reporting', cmd: 'report --weekly',
    before: ['raw data, 5 dashboards', 'CSV stitching', 'every Monday'],
    steps: ['pull + join the sources', 'flag what actually moved', 'write it in plain English'],
    after: ['the Monday brief', 'specific, not noisy', 'on your desk at 8am'],
    saved: '~6 hrs / week',
  },
  {
    id: 'schedule', tag: 'scheduling', cmd: 'schedule --resolve',
    before: ['5 reschedule requests', '1 shared calendar', 'endless back-and-forth'],
    steps: ['read every constraint', 'negotiate the slots', 'send confirmations'],
    after: ['everyone booked', 'zero ping-pong', 'conflicts resolved'],
    saved: '~4 hrs / week',
  },
];

function injectStyles() {
  if (typeof document === 'undefined' || document.getElementById('kz-automate-styles')) return;
  const s = document.createElement('style');
  s.id = 'kz-automate-styles';
  s.textContent = `
    .ax-win{ border:1px solid var(--line-strong); background:var(--bg-1); margin-top:24px; }
    .ax-board{
      display:grid; grid-template-columns:1fr auto 1fr auto 1fr;
      gap:0; align-items:stretch;
    }
    .ax-col{ padding:18px 18px 16px; min-width:0; overflow:hidden; }
    .ax-col + .ax-arr + .ax-col{ border-left:1px solid var(--line); }
    .ax-col-mid{ border-left:1px solid var(--line); border-right:1px solid var(--line); }
    .ax-arr{ display:flex; align-items:center; justify-content:center; color:var(--amber); font-size:16px; opacity:0.55; padding:0 4px; }
    .ax-eyebrow{ font-size:9px; letter-spacing:0.2em; text-transform:uppercase; margin-bottom:12px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .ax-eyebrow.manual{ color:var(--red); }
    .ax-eyebrow.agent{ color:var(--amber); }
    .ax-eyebrow.auto{ color:var(--green); }
    .ax-line{
      font-size:12px; line-height:1.5; color:var(--fg-2);
      display:flex; gap:8px; align-items:flex-start;
      padding:3px 0; overflow-wrap:anywhere; word-break:break-word;
    }
    .ax-line .mk{ flex:none; opacity:0.8; }
    .ax-col-before .ax-line{ color:var(--fg-3); }
    .ax-col-before .mk{ color:var(--red); }
    /* agent steps light up one by one */
    .ax-step{ opacity:0.25; transition:opacity 240ms ease, color 240ms ease; color:var(--fg-3); }
    .ax-step .mk{ color:var(--amber); }
    .ax-step.on{ opacity:1; color:var(--fg); }
    .ax-step.done .mk::before{ content:'✓'; }
    .ax-step .mk::before{ content:'▸'; }
    .ax-col-after .ax-line{ opacity:0; transform:translateY(4px); transition:opacity 300ms ease, transform 300ms ease; color:var(--fg-2); }
    .ax-col-after .mk{ color:var(--green); }
    .ax-col-after.show .ax-line{ opacity:1; transform:none; }
    .ax-progress{ height:3px; background:var(--bg-3); margin-top:12px; overflow:hidden; }
    .ax-progress > i{ display:block; height:100%; width:0; background:var(--amber); box-shadow:0 0 8px color-mix(in oklch, var(--amber) 50%, transparent); }
    .ax-saved{
      margin-top:12px; font-size:11px; letter-spacing:0.08em; color:var(--green);
      opacity:0; transition:opacity 300ms ease; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
    }
    .ax-col-after.show .ax-saved{ opacity:1; }
    .ax-tabs{ display:flex; gap:8px; flex-wrap:wrap; }
    .ax-tab{
      font-size:10px; letter-spacing:0.1em; text-transform:uppercase;
      color:var(--fg-4); border:1px solid var(--line); background:transparent;
      padding:4px 9px; cursor:pointer; white-space:nowrap; transition:color 140ms ease, border-color 140ms ease;
    }
    .ax-tab:hover{ color:var(--amber); }
    .ax-tab.is-active{ color:var(--bg); background:var(--amber); border-color:var(--amber); }
    .ax-cursor{ display:inline-block; width:7px; height:12px; background:var(--amber); margin-left:2px; vertical-align:-2px; animation:axblink 1.05s steps(2,start) infinite; }
    @keyframes axblink{ to{ opacity:0.15; } }
    .ax-cta{
      margin-top:18px; border:1px solid rgba(124,58,237,0.5);
      background:color-mix(in oklch, #7C3AED 6%, transparent);
      padding:16px 20px; display:flex; flex-wrap:wrap; gap:12px 18px;
      align-items:center; justify-content:space-between;
    }
    .ax-cta .q{ font-size:13px; color:var(--fg-2); }
    .ax-cta .q b{ color:#a78bfa; font-weight:600; }
    .ax-cta a{
      font-family:var(--mono); font-size:11px; letter-spacing:0.14em; text-transform:uppercase;
      color:#a78bfa; text-decoration:none; border:1px solid rgba(124,58,237,0.6);
      padding:9px 15px; white-space:nowrap; transition:background 140ms ease, color 140ms ease, border-color 140ms ease;
    }
    .ax-cta a:hover{ background:#7C3AED; color:#fff; border-color:#7C3AED; }
    @media (max-width:760px){
      .ax-board{ grid-template-columns:1fr; }
      .ax-arr{ padding:8px 0; transform:rotate(90deg); }
      .ax-col-mid{ border-left:0; border-right:0; border-top:1px solid var(--line); border-bottom:1px solid var(--line); }
      .ax-col + .ax-arr + .ax-col{ border-left:0; }
    }
    @media (prefers-reduced-motion: reduce){
      .ax-step{ opacity:1; color:var(--fg); }
      .ax-col-after .ax-line{ opacity:1; transform:none; }
      .ax-col-after .ax-saved{ opacity:1; }
      .ax-cursor{ animation:none; }
    }
  `;
  document.head.appendChild(s);
}

function usePRM() {
  const read = () => typeof window !== 'undefined' && window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [r, setR] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setR(m.matches);
    if (m.addEventListener) { m.addEventListener('change', on); return () => m.removeEventListener('change', on); }
    m.addListener(on); return () => m.removeListener(on);
  }, []);
  return r;
}

function AutomateExplainer() {
  const reduced = usePRM();
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('work'); // work | done
  const [litSteps, setLitSteps] = useState(reduced ? 3 : 0);
  const sectionRef = useRef(null);
  const visible = useRef(true);
  const timers = useRef([]);
  const barRef = useRef(null);

  useEffect(() => { injectStyles(); }, []);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  // visibility gate — don't animate offscreen / hidden tab
  useEffect(() => {
    if (reduced || typeof IntersectionObserver === 'undefined') return;
    const node = sectionRef.current;
    if (!node) return;
    const obs = new IntersectionObserver((es) => es.forEach((e) => { visible.current = e.isIntersecting; }), { threshold: 0.15 });
    obs.observe(node);
    const onVis = () => { if (document.hidden) visible.current = false; };
    document.addEventListener('visibilitychange', onVis);
    return () => { obs.disconnect(); document.removeEventListener('visibilitychange', onVis); };
  }, [reduced]);

  // the cycle engine
  useEffect(() => {
    if (reduced) return;
    clearTimers();
    setPhase('work');
    setLitSteps(0);
    if (barRef.current) { barRef.current.style.transition = 'none'; barRef.current.style.width = '0%'; }

    const a = AUTOMATIONS[idx];
    const stepGap = 760;
    // light each step in turn
    a.steps.forEach((_, i) => {
      timers.current.push(setTimeout(() => setLitSteps(i + 1), 350 + i * stepGap));
    });
    // progress bar fills across the work phase
    timers.current.push(setTimeout(() => {
      if (barRef.current) { barRef.current.style.transition = `width ${a.steps.length * stepGap}ms linear`; barRef.current.style.width = '100%'; }
    }, 360));
    // resolve to "done"
    const workEnd = 350 + a.steps.length * stepGap + 250;
    timers.current.push(setTimeout(() => setPhase('done'), workEnd));
    // hold, then advance (skip if not visible — recheck and extend)
    const advance = () => {
      if (!visible.current) { timers.current.push(setTimeout(advance, 900)); return; }
      setIdx((i) => (i + 1) % AUTOMATIONS.length);
    };
    timers.current.push(setTimeout(advance, workEnd + 2600));

    return clearTimers;
  }, [idx, reduced]);

  const a = AUTOMATIONS[idx];
  const pick = (i) => { clearTimers(); setIdx(i); };

  return (
    <section data-section="automate" className="section-pad" ref={sectionRef} style={{ paddingTop: 28 }}>
      <div className="container">
        <div className="box-head">
          <div className="rule" />
          <span>ai automation · what it actually looks like</span>
          <div className="rule right" />
        </div>

        <h2 className="h-section" style={{ marginBottom: 8 }}>
          AI doesn't replace your team. It deletes the busywork.
          <br />
          <span className="dim2" style={{ fontWeight: 600 }}>{'>'} watch five real workflows automate themselves.</span>
        </h2>

        <div className="ax-win win">
          <div className="win-title">
            <div className="lights"><span className="r" /><span className="a" /><span className="g" /></div>
            <span>ai-automation.sh</span>
            <span className="meta" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              <span className="dim">$</span>
              <span className="amber">{a.cmd}</span>
              {!reduced && phase === 'work' && <span className="ax-cursor" />}
            </span>
          </div>

          <div className="ax-board">
            {/* MANUAL — the pain */}
            <div className="ax-col ax-col-before">
              <div className="ax-eyebrow manual">the manual way</div>
              {a.before.map((b, i) => (
                <div className="ax-line" key={i}><span className="mk">✕</span><span>{b}</span></div>
              ))}
            </div>

            <div className="ax-arr">→</div>

            {/* AI AGENT — working */}
            <div className="ax-col ax-col-mid">
              <div className="ax-eyebrow agent">ai agent · working</div>
              {a.steps.map((st, i) => (
                <div className={'ax-line ax-step' + (i < litSteps ? ' on' : '')} key={i}>
                  <span className={'mk' + (i < litSteps ? ' done' : '')} />
                  <span>{st}</span>
                </div>
              ))}
              <div className="ax-progress"><i ref={barRef} /></div>
            </div>

            <div className="ax-arr">→</div>

            {/* AUTOMATED — the result */}
            <div className={'ax-col ax-col-after' + (phase === 'done' ? ' show' : '')}>
              <div className="ax-eyebrow auto">automated · done</div>
              {a.after.map((af, i) => (
                <div className="ax-line" key={i} style={{ transitionDelay: (i * 90) + 'ms' }}><span className="mk">✓</span><span>{af}</span></div>
              ))}
              <div className="ax-saved">▸ time back: {a.saved}</div>
            </div>
          </div>

          <div className="win-foot" style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div className="ax-tabs">
              {AUTOMATIONS.map((x, i) => (
                <button key={x.id} className={'ax-tab' + (i === idx ? ' is-active' : '')} onClick={() => pick(i)} type="button">{x.tag}</button>
              ))}
            </div>
            <span className="dim" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase' }}>one operator · AI agent teams · zero new hires</span>
          </div>
        </div>

        {/* educational note + ReadyIQ funnel */}
        <p className="mt-16" style={{ fontSize: 12, color: 'var(--fg-3)', maxWidth: '70ch', lineHeight: 1.6 }}>
          Every one of these is a real workflow an AI agent runs in production today. The hard part was never the AI — it was knowing <span style={{ color: '#a78bfa' }}>which of your workflows to automate first</span>, in what order, with what guardrails.
        </p>

        <div className="ax-cta">
          <span className="q">That map is exactly what <b>ReadyIQ</b> builds — start with the free readiness scorecard.</span>
          <a href="https://www.readyiq.ai/book" target="_blank" rel="noreferrer">▸ book an AI Audit</a>
        </div>
      </div>
    </section>
  );
}

Object.assign(window, { AutomateExplainer });

export { AutomateExplainer };
